import { osnovaApi } from '../base';
import type {
  BdrAnomaly,
  BdrGridItem,
  BdrPeriodHeader,
  BdrPeriodView,
  BdrStatusInfo,
} from '@entities/economic/bdrV2';

/**
 * БДР v2 Экономиста — `/agents/econ/bdr`. При выключенном флаге
 * `OSNOVA_ECON_V2_ENABLED` отвечает только `status` (остальное — 404), по нему
 * экран показывает заглушку. Ход сбора приходит WS-событием `econ_bdr_updated`
 * (rtkBridge инвалидирует тег `EconBdr`).
 */
export const econBdrApi = osnovaApi.injectEndpoints({
  endpoints: (b) => ({
    getEconBdrStatus: b.query<BdrStatusInfo, void>({
      query: () => '/agents/econ/bdr/status',
      providesTags: ['EconBdr'],
    }),
    listEconBdrPeriods: b.query<
      { from: string; to: string; items: BdrGridItem[] },
      { from?: string; to?: string; scope?: string }
    >({
      query: (params) => ({ url: '/agents/econ/bdr/periods', params }),
      providesTags: ['EconBdr'],
    }),
    getEconBdrPeriod: b.query<BdrPeriodView, string>({
      query: (id) => `/agents/econ/bdr/periods/${id}`,
      providesTags: ['EconBdr'],
    }),
    collectEconBdrPeriod: b.mutation<
      { period: BdrPeriodHeader; created: boolean; queued: boolean },
      { month: string; scope?: string; refresh_chronum?: boolean }
    >({
      query: ({ month, scope, refresh_chronum }) => ({
        url: `/agents/econ/bdr/periods/${month}/collect`,
        method: 'POST',
        body: { scope, refresh_chronum: refresh_chronum ?? false },
      }),
      invalidatesTags: ['EconBdr'],
    }),
    transitionEconBdrPeriod: b.mutation<
      { period: BdrPeriodHeader },
      { id: string; action: 'submit' | 'approve' | 'reopen'; comment?: string }
    >({
      query: ({ id, action, comment }) => ({
        url: `/agents/econ/bdr/periods/${id}/${action}`,
        method: 'POST',
        body: { comment: comment ?? null },
      }),
      invalidatesTags: ['EconBdr'],
    }),
    editEconBdrLine: b.mutation<
      unknown,
      { id: string; value: string | null; reason: string | null; expected_updated_at: string }
    >({
      query: ({ id, ...body }) => ({ url: `/agents/econ/bdr/lines/${id}`, method: 'PATCH', body }),
      invalidatesTags: ['EconBdr'],
    }),
    getEconBdrLineTrace: b.query<Record<string, unknown>, string>({
      query: (id) => `/agents/econ/bdr/lines/${id}/trace`,
      providesTags: ['EconBdr'],
    }),
    decideEconBdrAnomaly: b.mutation<
      { anomaly: BdrAnomaly },
      { id: string; decision: 'accepted' | 'rejected' | null; comment?: string }
    >({
      query: ({ id, decision, comment }) => ({
        url: `/agents/econ/bdr/anomalies/${id}`,
        method: 'PATCH',
        body: { decision, comment: comment ?? null },
      }),
      invalidatesTags: ['EconBdr'],
    }),
  }),
});

export const {
  useGetEconBdrStatusQuery,
  useListEconBdrPeriodsQuery,
  useGetEconBdrPeriodQuery,
  useCollectEconBdrPeriodMutation,
  useTransitionEconBdrPeriodMutation,
  useEditEconBdrLineMutation,
  useGetEconBdrLineTraceQuery,
  useDecideEconBdrAnomalyMutation,
} = econBdrApi;
